import { useEffect, useState } from "react";
import { ChartLineUp, Warning, PencilSimple, Check, ArrowsClockwise, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => Number(n || 0).toLocaleString();

// Small stat tile — used for both the Challenge Activity row and the
// Fundraising row. Kept intentionally compact so the whole dashboard hero
// fits above the fold on a 1440-wide laptop.
const StatTile = ({ label, value, testId, tone = "default", sub }) => (
  <div
    data-testid={testId}
    className={`border ${
      tone === "hero"
        ? "border-[#FF1B91] bg-gradient-to-br from-[#FF1B91]/10 to-transparent"
        : tone === "muted"
        ? "border-white/10 bg-white/[0.02]"
        : "border-white/15 bg-[#0F0F0F]"
    } px-4 py-3 flex flex-col justify-between min-h-[92px]`}
  >
    <div className={`text-[9px] uppercase tracking-[0.3em] font-bold ${tone === "hero" ? "text-[#FF1B91]" : "text-white/50"}`}>
      {label}
    </div>
    <div className={`font-display font-black ${tone === "hero" ? "text-3xl text-white" : "text-2xl text-white/90"} leading-none mt-2 tabular-nums`}>
      {value}
    </div>
    {sub && (
      <div className="text-[10px] text-white/45 mt-1.5 tracking-wide">{sub}</div>
    )}
  </div>
);

export default function FundraisingSummaryPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pledgeDraft, setPledgeDraft] = useState("");
  const [pledgeSaving, setPledgeSaving] = useState(false);
  const [pledgeMsg, setPledgeMsg] = useState("");
  const [editing, setEditing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestMsg, setDigestMsg] = useState("");

  const fetchSummary = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/admin/fundraising/summary`, {
        headers: { "X-Admin-Token": token },
      });
      if (r.ok) {
        const d = await r.json();
        setData(d);
        setPledgeDraft(String(d?.pledge?.total ?? ""));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSummary(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const savePledge = async () => {
    const amt = parseFloat(pledgeDraft);
    if (Number.isNaN(amt) || amt < 0) {
      setPledgeMsg("Enter a number ≥ 0");
      setTimeout(() => setPledgeMsg(""), 3500);
      return;
    }
    setPledgeSaving(true);
    setPledgeMsg("");
    try {
      const r = await fetch(`${BACKEND}/api/admin/fundraising/pledge-total`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-Admin-Token": token },
        body: JSON.stringify({ amount: amt }),
      });
      if (r.ok) {
        setPledgeMsg("Saved ✓");
        setEditing(false);
        await fetchSummary();
      } else {
        setPledgeMsg(`HTTP ${r.status}`);
      }
    } catch (e) {
      setPledgeMsg(`Error: ${e?.message || "network"}`);
    } finally {
      setPledgeSaving(false);
      setTimeout(() => setPledgeMsg(""), 3500);
    }
  };

  const syncPledge = async () => {
    setSyncing(true);
    setPledgeMsg("");
    try {
      const r = await fetch(`${BACKEND}/api/admin/fundraising/pledge-sync`, {
        method: "POST",
        headers: { "X-Admin-Token": token },
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        setPledgeMsg(`Synced $${Number(d.total).toFixed(2)} ✓`);
        await fetchSummary();
      } else {
        setPledgeMsg(`Sync failed: ${d.error || `HTTP ${r.status}`}`);
      }
    } catch (e) {
      setPledgeMsg(`Sync error: ${e?.message || "network"}`);
    } finally {
      setSyncing(false);
      setTimeout(() => setPledgeMsg(""), 5000);
    }
  };

  const sendDigestNow = async () => {
    if (!window.confirm("Send the weekly digest email now to sweatwithslay@gmail.com?")) return;
    setSendingDigest(true);
    setDigestMsg("");
    try {
      const r = await fetch(`${BACKEND}/api/admin/digest/send?force=true`, {
        method: "POST",
        headers: { "X-Admin-Token": token },
      });
      const d = await r.json();
      if (r.ok && d.sent) {
        setDigestMsg(`Sent to ${d.to} ✓ · ${d.new_signups} new signups, ${d.weekly_minutes} min this week`);
      } else if (r.ok && !d.sent) {
        setDigestMsg(`Not sent: ${d.reason || "check Resend key"}`);
      } else {
        setDigestMsg(`HTTP ${r.status}: ${d.detail || "failed"}`);
      }
    } catch (e) {
      setDigestMsg(`Error: ${e?.message || "network"}`);
    } finally {
      setSendingDigest(false);
      setTimeout(() => setDigestMsg(""), 10000);
    }
  };

  const act = data?.challenge_activity || {};
  const pledge = data?.pledge || {};
  const split = data?.split_5050 || {};
  const attention = data?.needs_attention || {};
  const totalRaised = data?.total_raised ?? 0;
  const isAuto = pledge.source === "auto";
  const pledgeUpdated = pledge.updated_at
    ? new Date(pledge.updated_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "never — enter your Pledge campaign total below";
  const autoLastSync = pledge.auto_last_sync
    ? new Date(pledge.auto_last_sync).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : null;

  return (
    <CollapsibleSection
      id="fundraising-summary"
      testId="fundraising-summary-panel"
      title="Dashboard"
      icon={<ChartLineUp size={22} weight="bold" className="text-[#FF1B91]" />}
      subtitle="Challenge activity · Fundraising · What needs attention"
      onRefresh={fetchSummary}
      defaultOpen
    >
      {!data && loading && (
        <div className="text-white/50 text-sm py-8 text-center">Loading dashboard…</div>
      )}
      {data && (
        <div className="space-y-6">
          {/* Row 1 — Challenge Activity */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] text-white/40 font-bold mb-2">Challenge Activity</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile
                testId="stat-registered"
                label="Participants"
                value={num(act.total_registered)}
                sub={act.hidden ? `${num(act.hidden)} hidden from the board` : "all on the leaderboard"}
              />
              <StatTile
                testId="stat-finished-911"
                label="Finished 911"
                value={num(act.finished_911)}
                sub="completed the challenge"
              />
              <StatTile
                testId="stat-still-working"
                label="Still Working"
                value={num(act.still_working)}
                sub={`${num(act.yet_to_start)} yet to start`}
              />
              <StatTile
                testId="stat-minutes"
                label="Minutes Logged"
                value={num(act.total_minutes_logged)}
                sub={`of ${num((act.total_registered || 0) * 911)} possible`}
              />
            </div>
          </div>

          {/* Row 2 — Fundraising. Donations drive the 50/50 charity split;
              Total Raised is GROSS (donations + merch gross). Merch net and
              print costs live in the Merch Orders panel for accounting. */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] text-white/40 font-bold mb-2">Fundraising</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile
                testId="stat-donations"
                label="Donations"
                value={money(pledge.total)}
                tone="hero"
                sub={isAuto ? `AUTO · synced ${autoLastSync || pledgeUpdated}` : `MANUAL · updated ${pledgeUpdated}`}
              />
              <StatTile
                testId="stat-split-t2t"
                label="Tunnel to Towers"
                value={money(split.tunnel_to_towers)}
                sub="50% of donations"
              />
              <StatTile
                testId="stat-split-fdny"
                label="FDNY Fire Family Transport"
                value={money(split.fdny_fire_family_transport)}
                sub="50% of donations"
              />
              <StatTile
                testId="stat-total-raised"
                label="Total Raised (gross)"
                value={money(totalRaised)}
                tone="muted"
                sub="donations + merch gross"
              />
            </div>
          </div>

          {/* Pledge controls — sync now + manual override */}
          <div className="border border-white/10 bg-white/[0.02] px-4 py-3 flex items-center gap-3 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-bold">Pledge</div>
            <button
              type="button"
              data-testid="pledge-sync-btn"
              onClick={syncPledge}
              disabled={syncing}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-bold border border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40 px-3 py-1.5"
            >
              <ArrowsClockwise size={12} weight="bold" className={syncing ? "animate-spin" : ""} />
              {syncing ? "Syncing…" : "Sync from Pledge now"}
            </button>
            {!editing ? (
              <button
                type="button"
                data-testid="pledge-edit-btn"
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/20 hover:border-[#FF1B91] text-white/70 hover:text-white px-3 py-1.5"
              >
                <PencilSimple size={12} weight="bold" /> Manual override
              </button>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white/60 text-sm">$</span>
                <input
                  data-testid="pledge-total-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={pledgeDraft}
                  onChange={(e) => setPledgeDraft(e.target.value)}
                  className="bg-black border border-white/20 focus:border-[#FF1B91] outline-none px-3 py-1.5 text-sm text-white w-36 tabular-nums"
                  autoFocus
                />
                <button
                  type="button"
                  data-testid="pledge-save-btn"
                  onClick={savePledge}
                  disabled={pledgeSaving}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-bold border border-[#FF1B91] text-white bg-[#FF1B91]/10 hover:bg-[#FF1B91]/20 disabled:opacity-40 px-3 py-1.5"
                >
                  <Check size={12} weight="bold" /> {pledgeSaving ? "…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(false); setPledgeDraft(String(pledge.total ?? "")); }}
                  className="text-[10px] uppercase tracking-[0.25em] text-white/50 hover:text-white/80"
                >
                  Cancel
                </button>
              </div>
            )}
            <div className="text-white/40 text-[11px] flex-1 min-w-[220px]">
              Auto-syncs every 6h from your public Pledge widget page. Override manually anytime.
            </div>
            {pledgeMsg && (
              <span data-testid="pledge-msg" className="text-[10px] uppercase tracking-[0.25em] text-[#FF1B91]">{pledgeMsg}</span>
            )}
          </div>

          {/* Weekly digest — auto Fri 9am ET, manual anytime */}
          <div className="border border-white/10 bg-white/[0.02] px-4 py-3 flex items-center gap-3 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-bold">Weekly Digest</div>
            <button
              type="button"
              data-testid="digest-send-btn"
              onClick={sendDigestNow}
              disabled={sendingDigest}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-bold border border-[#FF1B91] text-white bg-[#FF1B91]/10 hover:bg-[#FF1B91]/20 disabled:opacity-40 px-3 py-1.5"
            >
              <PaperPlaneTilt size={12} weight="bold" />
              {sendingDigest ? "Sending…" : "Send digest now"}
            </button>
            <div className="text-white/40 text-[11px] flex-1 min-w-[220px]">
              Auto-sent every Friday 9am ET to <span className="text-white/70">sweatwithslay@gmail.com</span>. Includes new signups, minutes logged, and dollars raised.
            </div>
            {digestMsg && (
              <span data-testid="digest-msg" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-emerald-300">
                <CheckCircle size={12} weight="bold" /> {digestMsg}
              </span>
            )}
          </div>

          {/* Needs attention */}
          {attention.paid_not_fulfilled > 0 && (
            <div
              data-testid="needs-attention"
              className="border border-yellow-500/60 bg-yellow-500/[0.06] px-4 py-3 flex items-center gap-3"
            >
              <Warning size={22} weight="bold" className="text-yellow-400 shrink-0" />
              <div className="flex-1">
                <div className="text-yellow-200 text-sm font-bold">
                  {attention.paid_not_fulfilled} paid order{attention.paid_not_fulfilled === 1 ? "" : "s"} need{attention.paid_not_fulfilled === 1 ? "s" : ""} attention
                </div>
                <div className="text-white/50 text-xs mt-0.5">
                  Paid but not yet confirmed in Printful (drafts pending your approval on Printful.com, or push failed). Open the Merch Orders panel below.
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
}
